import React, { useEffect, useMemo, useRef } from "react";
import {
  NIGHTLY_CHECK_IN_TIME, NIGHTLY_CHECK_OUT_TIME,
  DEFAULT_NIGHTS, DEFAULT_HOURS, DEFAULT_DAYS,
  defaultStartForMode, endForMode, countForMode,
  dateAtTime, extractDate, parseStayDateTime,
} from "../utils/billing";

// ─────────────────────────────────────────────────────────────────────────
//  Guided check-in / check-out picker.
//
//  Why this exists
//  ---------------
//  Operators picking arbitrary datetime values for a "nightly" stay led to
//  inconsistent windows (3 AM check-ins, same-day check-outs, etc.).  The
//  user wanted the system to lock the times to the chosen billing mode so
//  the operator can only meaningfully edit what's actually variable.
//
//  Mode contracts
//  --------------
//  • nightly — date + N nights.  Times are pinned (arrive 6 PM, depart 7 AM
//             next morning).  Operator picks the check-in date and how
//             many nights; both check-in and check-out date+time are
//             derived.
//
//  • hourly  — datetime + N hours.  Operator picks the start datetime and
//             a duration (0.5 h step).  Check-out is start + duration.
//
//  • daily   — datetime + N days.  Operator picks the start; check-out is
//             exactly start + (N × 24h), same wall-clock time.  Operator
//             only edits the day count, not the checkout time.
//
//  Props
//  -----
//  mode      : "nightly" | "hourly" | "daily"
//  checkIn   : controlled datetime-local string (or "")
//  checkOut  : controlled datetime-local string (or "")
//  onChange  : ({ check_in_at, check_out_at }) => void
//              Fires whenever any input updates.  Always emits BOTH so the
//              parent's form state stays internally consistent.
//  disabled  : optional — used in read-only contexts (edit-on-current-stay)
//
//  This component never touches the parent's billing_mode — flipping modes
//  is the parent's job.  When the parent changes `mode`, we snap the
//  window into the new mode's defaults so the operator sees coherent
//  times immediately.
// ─────────────────────────────────────────────────────────────────────────
export default function StayWindowPicker({
  mode, checkIn, checkOut, onChange, disabled = false,
}) {
  // Current count (nights/hours/days) is derived from the controlled
  // values.  We don't keep an internal copy; whenever the operator types
  // a new count we recompute the checkout and emit it.
  const count = useMemo(
    () => countForMode(mode, checkIn, checkOut),
    [mode, checkIn, checkOut]
  );

  // When the mode changes, snap the window onto that mode's defaults.
  // We compare against the *previous* mode so we don't snap on every
  // re-render — only when the operator actually toggles modes.
  const prevMode = useRef(mode);
  useEffect(() => {
    if (prevMode.current === mode) return;
    prevMode.current = mode;
    const start = checkIn || defaultStartForMode(mode);
    const startForMode = mode === "nightly"
      ? dateAtTime(start, NIGHTLY_CHECK_IN_TIME)
      : start;
    const fresh = endForMode(
      mode,
      startForMode,
      mode === "hourly" ? DEFAULT_HOURS : mode === "daily" ? DEFAULT_DAYS : DEFAULT_NIGHTS,
    );
    onChange({ check_in_at: startForMode, check_out_at: fresh });
    // We intentionally exclude `count` here — re-snapping only on a mode
    // change, not on a count change inside the same mode.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // First mount with no values — populate sensible defaults so the form
  // doesn't show blank fields when the operator opens "New reservation".
  useEffect(() => {
    if (checkIn || checkOut) return;
    const start = defaultStartForMode(mode);
    const end   = endForMode(mode, start);
    onChange({ check_in_at: start, check_out_at: end });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Change handlers ────────────────────────────────────────────────────
  const setCheckInDate = (yyyymmdd) => {
    if (!yyyymmdd) return;
    const start = dateAtTime(yyyymmdd, NIGHTLY_CHECK_IN_TIME);
    onChange({ check_in_at: start, check_out_at: endForMode(mode, start, count) });
  };
  const setCheckInDateTime = (dtLocal) => {
    if (!dtLocal) return;
    onChange({ check_in_at: dtLocal, check_out_at: endForMode(mode, dtLocal, count) });
  };
  const setCount = (raw) => {
    const n =
      mode === "hourly" ? Math.max(0.5, Number(raw) || 0.5) :
      mode === "daily"  ? Math.max(1,   Math.floor(Number(raw) || 1)) :
                          Math.max(1,   Math.floor(Number(raw) || 1));
    onChange({
      check_in_at:  checkIn || defaultStartForMode(mode),
      check_out_at: endForMode(mode, checkIn || defaultStartForMode(mode), n),
    });
  };

  // ── Common display helpers ────────────────────────────────────────────
  const fmt = (dtLocal) => {
    const d = parseStayDateTime(dtLocal);
    if (!d) return "—";
    return d.toLocaleString(undefined, {
      weekday: "short", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  };

  // ── Render per mode ────────────────────────────────────────────────────
  if (mode === "hourly") {
    return (
      <Wrap mode={mode} disabled={disabled}>
        <Field label="Check-in (start)">
          <input
            type="datetime-local"
            className="f-input"
            disabled={disabled}
            value={checkIn || ""}
            onChange={(e) => setCheckInDateTime(e.target.value)}
            required
          />
        </Field>
        <Field label="Hours">
          <div style={{ display: "flex", gap: 6 }}>
            <input
              type="number"
              min="0.5"
              step="0.5"
              className="f-input"
              disabled={disabled}
              value={count}
              onChange={(e) => setCount(e.target.value)}
              style={{ flex: 1 }}
            />
            {!disabled && (
              <>
                <Step onClick={() => setCount(count + 0.5)}>+30m</Step>
                <Step onClick={() => setCount(count + 1)}>+1h</Step>
              </>
            )}
          </div>
        </Field>
        <Recap kind="Depart" value={fmt(checkOut)}>
          {Number(count).toFixed(1)} hour{Number(count) === 1 ? "" : "s"} after check-in
        </Recap>
      </Wrap>
    );
  }

  if (mode === "daily") {
    return (
      <Wrap mode={mode} disabled={disabled}>
        <Field label="Check-in (start)">
          <input
            type="datetime-local"
            className="f-input"
            disabled={disabled}
            value={checkIn || ""}
            onChange={(e) => setCheckInDateTime(e.target.value)}
            required
          />
        </Field>
        <Field label="Days (24-hour blocks)">
          <div style={{ display: "flex", gap: 6 }}>
            <input
              type="number"
              min="1"
              step="1"
              className="f-input"
              disabled={disabled}
              value={count}
              onChange={(e) => setCount(e.target.value)}
              style={{ flex: 1 }}
            />
            {!disabled && (
              <>
                <Step onClick={() => setCount(count + 1)}>+1d</Step>
                <Step onClick={() => setCount(Math.max(1, count - 1))}>−1d</Step>
              </>
            )}
          </div>
        </Field>
        <Recap kind="Depart" value={fmt(checkOut)}>
          Exactly {count} × 24h after check-in — same time of day
        </Recap>
      </Wrap>
    );
  }

  // ── Nightly (default) ─────────────────────────────────────────────────
  return (
    <Wrap mode={mode} disabled={disabled}>
      <Field label="Check-in date">
        <input
          type="date"
          className="f-input"
          disabled={disabled}
          value={extractDate(checkIn)}
          onChange={(e) => setCheckInDate(e.target.value)}
          required
        />
      </Field>
      <Field label="Nights">
        <div style={{ display: "flex", gap: 6 }}>
          <input
            type="number"
            min="1"
            step="1"
            className="f-input"
            disabled={disabled}
            value={count}
            onChange={(e) => setCount(e.target.value)}
            style={{ flex: 1 }}
          />
          {!disabled && (
            <>
              <Step onClick={() => setCount(count + 1)}>+1</Step>
              <Step onClick={() => setCount(Math.max(1, count - 1))}>−1</Step>
            </>
          )}
        </div>
      </Field>
      <Recap kind="Arrive" value={fmt(checkIn)}>
        Locked to {pretty(NIGHTLY_CHECK_IN_TIME)} on the date you picked
      </Recap>
      <Recap kind="Depart" value={fmt(checkOut)}>
        {count} night{count === 1 ? "" : "s"} later · locked to {pretty(NIGHTLY_CHECK_OUT_TIME)}
      </Recap>
    </Wrap>
  );
}

// ───── Layout + small reusable subcomponents ────────────────────────────

function Wrap({ mode, disabled, children }) {
  return (
    <div style={{
      gridColumn: "1 / -1",
      padding: 14,
      background: "rgba(200,160,90,.05)",
      border: "1px dashed rgba(200,160,90,.3)",
      borderRadius: 10,
      opacity: disabled ? 0.7 : 1,
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 8,
        marginBottom: 12, fontSize: 11,
        letterSpacing: ".12em", textTransform: "uppercase",
        color: "var(--mid)",
      }}>
        <span>{MODE_LABEL[mode] || mode}</span>
        <span style={{ color: "#b8a090" }}>{MODE_HINT[mode]}</span>
      </div>
      <div style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 12,
      }}>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="f-group" style={{ marginBottom: 0 }}>
      <label className="f-label">{label}</label>
      {children}
    </div>
  );
}

function Step({ onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: "0 10px",
        background: "transparent",
        border: "1px solid rgba(180,140,80,.4)",
        borderRadius: 6,
        cursor: "pointer",
        fontSize: 11,
        fontWeight: 500,
        color: "#4a3828",
        whiteSpace: "nowrap",
      }}
    >{children}</button>
  );
}

function Recap({ kind, value, children }) {
  return (
    <div style={{
      gridColumn: "1 / -1",
      display: "flex",
      alignItems: "baseline",
      gap: 10,
      padding: "8px 12px",
      background: "#fff",
      border: "1px solid var(--border)",
      borderRadius: 7,
      fontSize: 12.5,
    }}>
      <span style={{
        fontSize: 10, letterSpacing: ".14em", textTransform: "uppercase",
        color: "var(--mid)", width: 56,
      }}>{kind}</span>
      <span style={{ color: "#2c1f14", fontWeight: 500 }}>{value}</span>
      <span style={{ color: "var(--mid)", marginLeft: "auto", fontSize: 11.5 }}>{children}</span>
    </div>
  );
}

const MODE_LABEL = {
  nightly: "Nightly stay",
  hourly:  "Hourly stay",
  daily:   "Daily stay",
};

const MODE_HINT = {
  nightly: `· Arrive ${pretty(NIGHTLY_CHECK_IN_TIME)} → depart ${pretty(NIGHTLY_CHECK_OUT_TIME)} next morning`,
  hourly:  "· Pick a start time and how many hours",
  daily:   "· 24-hour blocks, same wall-clock time both ends",
};

function pretty(time) {
  const [h, m] = String(time).split(":").map((n) => Number(n) || 0);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12  = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
}
